<script setup>
import PageHeaderUsageMeta from '@/components/ui/PageHeaderUsageMeta.vue'

defineProps({
	title: {
		type: String,
		required: true,
	},
	subtitle: {
		type: String,
		default: '',
	},
	// When true, keep title + actions on one row on mobile (still wraps if
	// content is too wide). Default keeps the existing stacked mobile layout.
	mobileInline: {
		type: Boolean,
		default: false,
	},
	// Breakpoint below which title/subtitle stack above the actions: 'sm'
	// (default) or 'lg' for headers whose actions and description are both
	// wide enough to fight over a landscape-phone / small-tablet row.
	stackBelow: {
		type: String,
		default: 'sm',
		validator: (v) => ['sm', 'lg'].includes(v),
	},
	// When true, keep the #meta slot visible on mobile. Default hides meta below md.
	showMetaOnMobile: {
		type: Boolean,
		default: false,
	},
	// When true, render #meta on mobile only (desktop keeps using the actions slot).
	metaMobileOnly: {
		type: Boolean,
		default: false,
	},
	// When true, keep the subtitle visible on mobile (still truncated). Default hides it below md.
	showSubtitleOnMobile: {
		type: Boolean,
		default: false,
	},
	// Optional plan usage chip: short label on mobile, full "X of Y … used" on desktop.
	usage: {
		type: Object,
		default: null,
	},
	fitMetaToRight: {
		type: Boolean,
		default: false,
	},
})
</script>

<template>
	<header
		class="flex gap-x-3 gap-y-3 mt-3 md:mt-11 mb-0 md:mb-6 pb-0 md:pb-5 md:shadow-[0_12px_16px_-14px_rgba(0,0,0,0.6)]"
		:class="mobileInline
			? 'flex-row flex-wrap items-center'
			: stackBelow === 'lg'
				? 'flex-col lg:flex-row lg:flex-wrap lg:items-center'
				: 'flex-col sm:flex-row sm:flex-wrap sm:items-center'"
	>
		<div
			class="flex items-center gap-x-3.5 min-w-0"
			:class="mobileInline ? 'w-auto flex-1' : stackBelow === 'lg' ? 'w-full lg:w-auto lg:flex-1' : 'w-full sm:w-auto sm:flex-1'"
		>
			<slot name="leading" />
			<div v-if="$slots.icon" class="page-header-icon shrink-0">
				<slot name="icon" />
			</div>
			<div class="flex min-w-0 flex-1 flex-col overflow-hidden">
				<div class="flex items-center gap-x-3 gap-y-1" :class="(showMetaOnMobile || metaMobileOnly) ? 'flex-nowrap' : 'flex-wrap'">
					<h1 class="page-header-title mb-0 min-w-0" :class="(showMetaOnMobile || metaMobileOnly) ? 'truncate' : ''">
						<slot name="title">{{ title }}</slot>
					</h1>
					<!-- Stat chips are secondary context — hide on mobile by default
					     to keep the header compact; opt in with showMetaOnMobile or `usage`. -->
					<div
						v-if="$slots.meta || usage"
						class="items-center gap-x-3 gap-y-1 flex-wrap shrink-0"
						:class="[metaMobileOnly ? 'flex sm:hidden' : showMetaOnMobile ? 'flex' : 'hidden md:flex', fitMetaToRight ? 'ml-auto' : '']"
					>
						<PageHeaderUsageMeta v-if="usage" v-bind="usage" />
						<slot name="meta" />
					</div>
				</div>
				<!-- Subtitle hidden on mobile by default to save vertical space.
				     Truncate so long copy cannot paint over the actions cluster. -->
				<p
					v-if="subtitle || $slots.subtitle"
					class="min-w-0 truncate text-surface-8 text-sm mt-1 mb-0"
					:class="showSubtitleOnMobile ? 'block' : 'hidden md:block'"
				>
					<slot name="subtitle">{{ subtitle }}</slot>
				</p>
			</div>
		</div>
		<div
			class="flex shrink-0 flex-wrap items-center gap-2"
			:class="mobileInline
				? 'w-auto ml-auto justify-end'
				: stackBelow === 'lg'
					? 'w-full lg:ml-auto lg:w-auto lg:justify-end'
					: 'w-full sm:ml-auto sm:w-auto sm:justify-end'"
		>
			<slot />
		</div>
	</header>
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
/* Crisper, more "enterprise" heading than the soft global .page-title
   (26px / 500 / 40px line-height reads too light + loose). */
.page-header-title {
	font-size: 20px;
	font-weight: 600;
	line-height: 1.2;
	letter-spacing: -0.015em;
	color: var(--c-surface-text-1, #fff);
	margin: 0;
}

@media (min-width: 640px) {
	.page-header-title {
		font-size: 24px;
	}
}
/* Leading section icon: a tinted, brand-colored square that anchors the header
   and makes it read like a designed product header. Only rendered when an
   `icon` slot is provided. */
.page-header-icon {
	width: 42px;
	height: 42px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 11px;
	background-color: var(--c-surface-3, #232d4d);
	border: 1px solid var(--c-surface-4, #2f3b5e);
	color: var(--c-main, #3e64ff);
}
.page-header-icon :deep(svg) {
	width: 22px;
	height: 22px;
}

</style>
